/**
 * FO-340 (batch-40) — gedeelde Zod-schemas + DTO's voor de twee statuslagen.
 *
 * Eén bron voor API en form (CLAUDE.md-validatieregel): de API `.parse`t
 * hiermee en het form valideert er client-side mee vóór verzenden, zodat het
 * form niets kán sturen dat de API weigert. Fouten renderen inline per veld via
 * `issuesToFieldErrors`; nooit een generieke `Validatiefout`-toast.
 *
 * De labels en de pure predicaten staan in `@streep/utils`
 * (`medewerker-selectie.ts`) — dit bestand gaat alleen over het CONTRACT.
 */
import { z } from 'zod'

// ─── Enums ──────────────────────────────────────────────────────────────────

export const DIENSTVERBAND_WAARDEN = ['CONCEPT', 'ONBOARDING', 'IN_DIENST', 'UIT_DIENST'] as const
export type Dienstverband = (typeof DIENSTVERBAND_WAARDEN)[number]

export const WERKSTATUS_WAARDEN = ['ACTIEF', 'ZIEK', 'LANGDURIG_ZIEK', 'NON_ACTIEF'] as const
export type Werkstatus = (typeof WERKSTATUS_WAARDEN)[number]

export const STATUS_LAAG_WAARDEN = ['DIENSTVERBAND', 'WERKSTATUS'] as const
export type StatusLaag = (typeof STATUS_LAAG_WAARDEN)[number]

export const STATUS_MUTATIE_BRON_WAARDEN = [
  'HR',
  'VERZUIM',
  'ONBOARDING',
  'OFFBOARDING',
  'CRON',
  'IMPORT',
  'MIGRATIE',
] as const
export type StatusMutatieBron = (typeof STATUS_MUTATIE_BRON_WAARDEN)[number]

export const dienstverbandEnum = z.enum(DIENSTVERBAND_WAARDEN)
export const werkstatusEnum = z.enum(WERKSTATUS_WAARDEN)

// ─── werkstatus is read-only (§11) ──────────────────────────────────────────

/**
 * De melding als iemand `werkstatus` tóch in een PATCH-body meestuurt. Het veld
 * is een cache-kolom die uit verzuim + non-actief-stelling wordt afgeleid; er
 * direct in schrijven zou de bron en de cache uit elkaar laten lopen tot de
 * eerstvolgende recompute 'm stilletjes terugdraait.
 */
export const WERKSTATUS_READONLY_MELDING =
  'Werkstatus wordt afgeleid uit verzuim en non-actief-stelling en kan niet direct gewijzigd worden.'

/**
 * Zod-veld dat `werkstatus` in een write-body afwijst met een INLINE veldfout op
 * `path: ['werkstatus']` (T23), i.p.v. 'm stil te negeren. Stil negeren zou een
 * HR-medewerker laten denken dat de wijziging is doorgevoerd.
 */
export const werkstatusReadonlyVeld = z.never({ error: WERKSTATUS_READONLY_MELDING }).optional()

// ─── Non-actief-stelling ────────────────────────────────────────────────────

/**
 * Non-actief = wel in dienst, tijdelijk niet inzetbaar (schorsing, sabbatical,
 * langdurig bijzonder verlof, wachtgeld). Reden is vrije tekst en NOOIT medisch
 * — daarvoor is de verzuimmodule.
 */
export const NonActiefRequestSchema = z
  .object({
    nonActiefSinds: z.coerce.date({ error: 'Geef een begindatum op.' }),
    nonActiefTot: z.coerce.date().nullable().optional(),
    nonActiefReden: z
      .string()
      .trim()
      .min(3, { message: 'Geef een reden op.' })
      .max(500, { message: 'Maximaal 500 tekens.' }),
  })
  .superRefine((val, ctx) => {
    if (val.nonActiefTot && val.nonActiefTot.getTime() < val.nonActiefSinds.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['nonActiefTot'],
        message: 'Einddatum ligt vóór de begindatum.',
      })
    }
  })
export type NonActiefRequest = z.infer<typeof NonActiefRequestSchema>

// ─── Dienstverband wijzigen ─────────────────────────────────────────────────

/**
 * De regels uit §11. `startDate`/`endDate` zitten in dezelfde body omdat ze
 * onlosmakelijk bij de overgang horen: IN_DIENST zonder startdatum en UIT_DIENST
 * zonder einddatum zijn allebei betekenisloos (en breken het peildatumvenster
 * waar klasse C op leunt).
 */
export const DienstverbandRequestSchema = z
  .object({
    dienstverband: dienstverbandEnum,
    startDate: z.coerce.date().nullable().optional(),
    endDate: z.coerce.date().nullable().optional(),
    reden: z.string().trim().max(500).optional(),
  })
  .superRefine((val, ctx) => {
    if (val.dienstverband === 'IN_DIENST' && !val.startDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['startDate'],
        message: 'Vul eerst de startdatum in.',
      })
    }
    if (val.dienstverband === 'UIT_DIENST' && !val.endDate) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Vul de datum uit dienst in.',
      })
    }
    if (val.startDate && val.endDate && val.endDate.getTime() < val.startDate.getTime()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['endDate'],
        message: 'Datum uit dienst ligt vóór de startdatum.',
      })
    }
  })
export type DienstverbandRequest = z.infer<typeof DienstverbandRequestSchema>

// ─── Read-DTO's ─────────────────────────────────────────────────────────────

/**
 * Read↔write-consistentie (de FO-333-regel): de read geeft deze velden terug,
 * mogelijk als `null`. De non-actief-velden zijn legitiem leeg (variant (a) uit
 * CLAUDE.md) → in het write-schema `.nullable()`. `werkstatus` is read-only en
 * wordt in het write-schema ge-`omit`.
 */
export interface MedewerkerStatusDTO {
  dienstverband: Dienstverband
  werkstatus: Werkstatus
  startDate: string | null
  endDate: string | null
  nonActiefSinds: string | null
  nonActiefTot: string | null
  nonActiefReden: string | null
}

export interface StatusMutatieDTO {
  id: string
  laag: StatusLaag
  van: string | null
  naar: string
  vanLabel: string | null
  naarLabel: string
  ingangsdatum: string
  reden: string | null
  bron: StatusMutatieBron
  actorNaam: string | null
  createdAt: string
}

/** Eén rij op de tijdelijke HR-reviewlijst (`/medewerkers/status-review`). */
export interface StatusReviewRijDTO {
  id: string
  employeeNumber: string
  naam: string
  /** De oude `status`-waarde waaruit gemigreerd is (dual-write-kolom). */
  oudeStatus: string
  dienstverband: Dienstverband
  werkstatus: Werkstatus
  startDate: string | null
  endDate: string | null
  /** Waarom deze rij twijfelachtig was — door de backfill-migratie gezet. */
  reviewReden: string
}

export const StatusReviewBevestigRequestSchema = z.object({
  employeeIds: z.array(z.string().min(1)).min(1, { message: 'Selecteer minimaal één medewerker.' }),
})
export type StatusReviewBevestigRequest = z.infer<typeof StatusReviewBevestigRequestSchema>

// ─── Labels voor de statushistorie ──────────────────────────────────────────

export const STATUS_MUTATIE_BRON_LABELS: Record<StatusMutatieBron, string> = {
  HR: 'HR',
  VERZUIM: 'Verzuim',
  ONBOARDING: 'Onboarding',
  OFFBOARDING: 'Uitdiensttreding',
  CRON: 'Automatisch',
  IMPORT: 'Import',
  MIGRATIE: 'Migratie',
}

export const STATUS_LAAG_LABELS: Record<StatusLaag, string> = {
  DIENSTVERBAND: 'Dienstverband',
  WERKSTATUS: 'Werkstatus',
}
